import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import API from '../../../services/API'

const journeyTime = (journey) => {
  const value =
    journey?.pickup?.when?.exactTime ||
    journey?.pickup?.when?.patientReadyFrom ||
    journey?.dropoff?.when?.exactTime ||
    journey?.dropoff?.when?.targetArrivalTime
  return value ? moment(value).format('ddd D MMM · HH:mm') : 'Time not set'
}

const stopName = (stop) =>
  stop?.name ||
  [stop?.town, stop?.postCode].filter(Boolean).join(', ') ||
  stop?.locationType ||
  '—'

/**
 * Review queue for hospital-portal transport requests (bookings that are
 * source 'Hospital Portal' and status Pending). Accepting confirms the
 * booking, which releases its journeys into the dispatch pool; rejecting
 * cancels all journeys (server cascade) and shows as Rejected in the portal.
 */
const PortalRequestReviewDialog = ({ open, onClose, onChanged }) => {
  const navigate = useNavigate()
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(false)
  const [actioningID, setActioningID] = useState(null)
  const [rejectingID, setRejectingID] = useState(null)
  const [rejectReason, setRejectReason] = useState('')
  const [error, setError] = useState(null)

  const loadRequests = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await API.get('/pts/portal-requests/pending')
      if (res.status === 200) {
        setRequests(res.data || [])
      } else {
        setError('Unable to load pending requests.')
      }
    } catch (e) {
      console.error(e)
      setError('Unable to load pending requests.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!open) return
    setRejectingID(null)
    setRejectReason('')
    loadRequests()
  }, [open])

  const updateStatus = async (request, newStatus, cancellationReason) => {
    setActioningID(request._id)
    setError(null)
    try {
      const res = await API.put(
        '/pts/bookings/update-status',
        {
          bookingID: request._id,
          newStatus,
          cancellationReason,
        },
        { skipGlobalErrorHandler: true },
      )
      if (res.status === 200) {
        setRequests((prev) =>
          prev.filter((pending) => pending._id !== request._id),
        )
        setRejectingID(null)
        setRejectReason('')
        onChanged?.()
      } else {
        setError(
          res.data?.error || 'Unable to update this request. Please try again.',
        )
      }
    } catch (e) {
      console.error(e)
      setError('Unable to update this request. Please try again.')
    } finally {
      setActioningID(null)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <LocalHospitalIcon color="warning" />
            <span>New transport requests</span>
            <Chip
              size="small"
              label={requests.length}
              color="warning"
              sx={{ fontWeight: 800, borderRadius: 1 }}
            />
          </Stack>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          {loading && <LinearProgress />}
          {error && <Alert severity="error">{error}</Alert>}
          {!loading && !requests.length && (
            <Alert severity="success">
              No transport requests waiting for review.
            </Alert>
          )}
          {requests.map((request) => {
            const requesterName = [
              request.requestedByUser?.firstName,
              request.requestedByUser?.lastName,
            ]
              .filter(Boolean)
              .join(' ')
            const patientName = [
              request.patient?.demographics?.firstName,
              request.patient?.demographics?.lastName,
            ]
              .filter(Boolean)
              .join(' ')
            const wardLabel = [
              request.wardDepartment?.name,
              request.wardDepartment?.internalLocation,
            ]
              .filter(Boolean)
              .join(' - ')
            const isRejecting = rejectingID === request._id
            const busy = actioningID === request._id

            return (
              <Paper
                key={request._id}
                variant="outlined"
                sx={{ p: 2, borderRadius: 2 }}
              >
                <Stack spacing={1}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ sm: 'center' }}
                    gap={1}
                  >
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography fontWeight={900}>
                          {request.bookingRef || 'New request'}
                        </Typography>
                        {request.eligibilityScreening?.completed && (
                          <Chip
                            size="small"
                            label={`Eligibility: ${request.eligibilityScreening.outcome}`}
                            color={
                              request.eligibilityScreening.outcome ===
                              'Eligible'
                                ? 'success'
                                : 'warning'
                            }
                            sx={{ borderRadius: 1 }}
                          />
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {[request.organisation?.orgName, wardLabel]
                          .filter(Boolean)
                          .join(' — ')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Requested {moment(request.createdAt).fromNow()}
                        {requesterName ? ` by ${requesterName}` : ''}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      endIcon={<OpenInNewIcon />}
                      onClick={() => {
                        onClose?.()
                        navigate(`/pts/bookings/${request._id}`)
                      }}
                    >
                      View booking
                    </Button>
                  </Stack>
                  <Divider />
                  <Stack spacing={0.5}>
                    <Typography variant="body2">
                      <b>Patient:</b> {patientName || 'Unknown'}
                      {request.patient?.demographics?.dob
                        ? ` · ${moment(request.patient.demographics.dob).format('DD/MM/YYYY')}`
                        : ''}
                      {request.patient?.mobility
                        ? ` · ${request.patient.mobility}`
                        : ''}
                    </Typography>
                    {(request.journeys || []).map((journey) => (
                      <Typography
                        key={journey._id}
                        variant="body2"
                        color="text.secondary"
                      >
                        {journey.journeyType || 'Journey'}:{' '}
                        {stopName(journey.pickup)} → {stopName(journey.dropoff)}{' '}
                        · {journeyTime(journey)}
                      </Typography>
                    ))}
                  </Stack>
                  {isRejecting ? (
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      gap={1}
                      alignItems={{ sm: 'center' }}
                    >
                      <TextField
                        size="small"
                        fullWidth
                        autoFocus
                        placeholder="Reason for rejection (shared with the hospital)"
                        value={rejectReason}
                        onChange={(event) => setRejectReason(event.target.value)}
                      />
                      <Stack direction="row" gap={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setRejectingID(null)
                            setRejectReason('')
                          }}
                        >
                          Back
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          disabled={!rejectReason.trim() || busy}
                          onClick={() =>
                            updateStatus(request, 'Rejected', rejectReason.trim())
                          }
                        >
                          Confirm rejection
                        </Button>
                      </Stack>
                    </Stack>
                  ) : (
                    <Stack direction="row" gap={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<HighlightOffIcon />}
                        disabled={busy}
                        onClick={() => {
                          setRejectingID(request._id)
                          setRejectReason('')
                        }}
                      >
                        Reject
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircleOutlineIcon />}
                        disabled={busy}
                        onClick={() => updateStatus(request, 'Confirmed')}
                      >
                        Accept
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Paper>
            )
          })}
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

export default PortalRequestReviewDialog
